import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Checkbox, Flex, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  AuthorizedButton,
  Container,
  DataTable,
  MotorInsuranceModal,
  PageTitle,
  SExportButton,
  SRangePicker,
} from '../../components';
import { tableActions } from '../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../store';
import useHttpService from '../../services/hook';
import { getColumns } from './columns';
import {
  validationSchemaForCancel,
  validationSchemaForCreate,
  validationSchemaForEdit,
} from './validations';
import { IExpandedRow } from '../../components/DataTable/type';
import { IEnum } from '../../services/type';
import {
  IFile,
  IWithViewPerm,
  IMotorInsuranceDataSource,
  IMotorInsuranceInitialValues,
} from '../../types';
import { modalTypes } from '../../assets/enums';
import globalStyles from '../../assets/styles/index.module.scss';
import { downloadFile } from '../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../utils/functions';
import useFetchParameters from '../../hooks';
import MotorInsuranceService from '../../api-clients/MotorInsuranceService';
import EnumService from '../../api-clients/EnumService';
import AuthService from '../../api-clients/AuthService';

const MotorInsurance: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedItem, setSelectedItem] = useState<
    IMotorInsuranceDataSource | undefined
  >(undefined);
  const [canceledItem, setCanceledItem] = useState<
    IMotorInsuranceDataSource | undefined
  >(undefined);
  const [addendumItem, setAddendumItem] = useState<
    IMotorInsuranceDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IMotorInsuranceDataSource[]>([]);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [isExpired, setIsExpired] = useState<boolean | undefined>(undefined);
  const [branches, setBranches] = useState<IEnum[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
    dates,
    isExpired,
  });
  const motorInsuranceService = useMemo(
    () => new MotorInsuranceService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchWithViewPerm = async () => {
    const response = await authService.fetchWithViewPerm(
      'motorinsurance',
      formik.values.organization,
    );
    if (response && !('statusText' in response))
      setWithViewPerm(response.results);
  };

  const handleFetchEnums = async () => {
    const response = await enumService.fetchMotorInsuranceBranches();
    if (response && Array.isArray(response)) {
      setBranches(response);
    }

    const response3 = await enumService.fetchMotorStatusChoices();
    if (response3 && Array.isArray(response3)) {
      setStatusChoices(response3);
    }
  };

  const handleFetchData = async () => {
    const response = await motorInsuranceService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<IMotorInsuranceDataSource[]>(
        (prev: IMotorInsuranceDataSource[], cur: IMotorInsuranceDataSource) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));
    const expandedRows = await motorInsuranceService.fetchExpandedRows(id);
    expandedRows &&
      Array.isArray(expandedRows) &&
      setExpandedRows(expandedRows);
  };

  const handleChangeExpired = () => setIsExpired((prev) => !prev);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await motorInsuranceService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('upload_files', e.target.files);
  };

  const handleClickCancel = async (id: number) => {
    const canceledItem = await motorInsuranceService.fetchItemById(id);
    if (canceledItem && 'id' in canceledItem) {
      setCanceledItem(canceledItem);
    }
  };

  const handleCloseCancel = () => {
    setCanceledItem(undefined);
  };

  const handleCloseAddendum = () => {
    setAddendumItem(undefined);
  };

  const handleClickAddendum = async (id: number) => {
    const selectedItem = await motorInsuranceService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setAddendumItem(selectedItem);
    }
  };

  const handleCancelPolicy = async () => {
    try {
      if (canceledItem) {
        await motorInsuranceService.cancelPolicy(
          canceledItem.id || 0,
          formik.values.upload_files[0],
          formik.values.description,
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IMotorInsuranceDataSource>(
        setIsModalOpen,
        setCanceledItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 10 }));
    }
  };

  const handleAddendumPolicy = async () => {
    try {
      if (addendumItem) {
        await motorInsuranceService.requestAddendum(
          addendumItem.id || 0,
          formik.values,
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IMotorInsuranceDataSource>(
        setIsModalOpen,
        setCanceledItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 10 }));
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await motorInsuranceService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IMotorInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 10 }));
    }
  };

  const handleClickExport = async () => {
    const response = await motorInsuranceService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IMotorInsuranceInitialValues) => {
    if (canceledItem) {
      handleCancelPolicy();
    } else if (addendumItem) {
      handleAddendumPolicy();
    } else {
      try {
        await motorInsuranceService.submitForm(values, selectedItem);
        if (selectedItem) {
          await motorInsuranceService.updateStatus(
            selectedItem.id || 0,
            values.recent_status,
            values.email_users || [],
          );
        }
      } catch (e) {
        console.log(e);
      } finally {
        cleanupAfterSubmit<IMotorInsuranceDataSource>(
          setIsModalOpen,
          setSelectedItem,
          formik.resetForm,
          handleFetchData,
        );
        dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
      }
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await motorInsuranceService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await motorInsuranceService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleClickCancel,
    handleClickAddendum,
    handleSetAdditionalFile,
    organizations,
    branches,
    statusChoices,
  );

  // Form Management
  const formik = useFormik<IMotorInsuranceInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema: addendumItem
      ? undefined
      : canceledItem
        ? validationSchemaForCancel
        : !selectedItem
          ? validationSchemaForCreate
          : validationSchemaForEdit,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IMotorInsuranceDataSource,
        IMotorInsuranceInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (canceledItem) {
      const result = prepareModalData<
        IMotorInsuranceDataSource,
        IMotorInsuranceInitialValues
      >(canceledItem);
      result &&
        formik.setValues({ ...result, description: '', upload_files: [] });
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [canceledItem]);

  useEffect(() => {
    if (addendumItem) {
      const result = prepareModalData<
        IMotorInsuranceDataSource,
        IMotorInsuranceInitialValues
      >(addendumItem);
      result &&
        formik.setValues({ ...result, description: '', upload_files: [] });
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [addendumItem]);

  useEffect(() => {
    handleFetchData();
  }, [isExpired, dates, search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Oto Poliçeler" />
      <Flex vertical gap="middle">
        <Flex justify="space-between">
          <Flex vertical gap="small">
            <SRangePicker
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              onChange={(_, dates: [string, string]) => setDates(dates)}
            />
            <Flex gap="small">
              <Checkbox checked={isExpired} onChange={handleChangeExpired} />
              <Typography className={globalStyles.outOfDateText}>
                Sadece Aktif Poliçeleri Göster
              </Typography>
            </Flex>
          </Flex>
          <Flex gap={5}>
            <SExportButton onClick={handleClickExport} />
            <AuthorizedButton
              model="motorinsurance"
              action="add"
              className={styles.button}
              onClick={handleOpenModal}
            >
              <PlusOutlined className={styles.buttonIcon} />
              <div className={styles.buttonText}>Yeni Poliçe Talebi</div>
            </AuthorizedButton>
          </Flex>
        </Flex>
        <DataTable<IMotorInsuranceDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <MotorInsuranceModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Poliçe Talebi"
        subTitle="Araç Sigortaları İçin Yeni Poliçe Talebi Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        branches={branches}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <MotorInsuranceModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Poliçe Talebi Güncelleme"
        subTitle="Araç Sigortaları İçin Poliçe Talebi Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        branches={branches}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <MotorInsuranceModal
        type={modalTypes.CANCEL}
        open={!!canceledItem}
        title="Poliçe İptal Talebi"
        subTitle="Araç Sigortaları İçin Poliçe İptal Talebi Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseCancel}
        branches={branches}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <MotorInsuranceModal
        type={modalTypes.ADDENDUM}
        open={!!addendumItem}
        title="Poliçe Zeyil Talebi"
        subTitle="Araç Sigortaları İçin Poliçe Zeyil Talebi Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseAddendum}
        branches={branches}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="motorinsuranceadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default MotorInsurance;
